import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'

import { LeasePet } from '../../../models/Lease/types'
import { ANIMAL_TYPES, DOG_BREEDS } from '../../LeasingSeasonConfig/petPolicy'
import { useReplaceLeadPets } from '../../../hooks/Outreach/useLeadCrm'

type PetDraft = {
    id?: number
    name: string
    animal_type: string
    breed: string
    weight: string
    notes: string
}

function toDraft(pet: LeasePet): PetDraft {
    return {
        id: pet.id,
        name: pet.name || '',
        animal_type: pet.animal_type || '',
        breed: pet.breed || '',
        weight: pet.weight || '',
        notes: pet.notes || '',
    }
}

function emptyPet(): PetDraft {
    return {
        name: '',
        animal_type: '',
        breed: '',
        weight: '',
        notes: '',
    }
}

interface Props {
    open: boolean
    leadId: number
    pets: LeasePet[]
    startWithNew?: boolean
    onClose: () => void
}

export const EditPetsDialog: React.FC<Props> = ({
    open,
    leadId,
    pets,
    startWithNew = false,
    onClose,
}) => {
    const { replacePets, isSaving } = useReplaceLeadPets(leadId)
    const [rows, setRows] = useState<PetDraft[]>([emptyPet()])

    useEffect(() => {
        if (!open) return
        const existing = pets.map(toDraft)
        if (startWithNew) {
            setRows(
                existing.length > 0 ? [...existing, emptyPet()] : [emptyPet()],
            )
        } else {
            setRows(existing.length > 0 ? existing : [emptyPet()])
        }
    }, [open, pets, startWithNew])

    const updateRow = (index: number, patch: Partial<PetDraft>) => {
        setRows((current) =>
            current.map((row, rowIndex) =>
                rowIndex === index ? { ...row, ...patch } : row,
            ),
        )
    }

    const handleSave = async () => {
        const payload = rows
            .filter((row) => row.name.trim() || row.animal_type.trim())
            .map((row) => ({
                id: row.id,
                name: row.name.trim(),
                animal_type: row.animal_type,
                breed: row.animal_type === 'dog' ? row.breed : '',
                weight: row.weight.trim() || null,
                notes: row.notes.trim(),
            }))
        try {
            await replacePets(payload)
            onClose()
        } catch {
            // Toast is raised by the mutation.
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle disableTypography>
                <Box display="flex" alignItems="center">
                    <Typography
                        variant="h6"
                        style={{ flex: 1, fontWeight: 700 }}
                    >
                        Edit pets
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {rows.map((row, index) => (
                    <Box
                        key={row.id ?? `new-${index}`}
                        mb={2}
                        p={1.5}
                        style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                        }}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                        >
                            <Typography
                                variant="caption"
                                style={{ fontWeight: 700 }}
                            >
                                Pet {index + 1}
                            </Typography>
                            <Button
                                size="small"
                                onClick={() =>
                                    setRows((current) => {
                                        const next = current.filter(
                                            (_, rowIndex) => rowIndex !== index,
                                        )
                                        return next.length > 0
                                            ? next
                                            : [emptyPet()]
                                    })
                                }
                            >
                                Remove
                            </Button>
                        </Box>
                        <TextField
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            label="Name"
                            value={row.name}
                            onChange={(event) =>
                                updateRow(index, { name: event.target.value })
                            }
                        />
                        <FormControl
                            fullWidth
                            variant="outlined"
                            margin="dense"
                        >
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={row.animal_type}
                                onChange={(event) =>
                                    updateRow(index, {
                                        animal_type: String(event.target.value),
                                        breed:
                                            event.target.value === 'dog'
                                                ? row.breed
                                                : '',
                                    })
                                }
                                label="Type"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {ANIMAL_TYPES.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {row.animal_type === 'dog' && (
                            <FormControl
                                fullWidth
                                variant="outlined"
                                margin="dense"
                            >
                                <InputLabel>Breed</InputLabel>
                                <Select
                                    value={row.breed}
                                    onChange={(event) =>
                                        updateRow(index, {
                                            breed: String(event.target.value),
                                        })
                                    }
                                    label="Breed"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {DOG_BREEDS.map((breed) => (
                                        <MenuItem
                                            key={breed.id}
                                            value={breed.id}
                                        >
                                            {breed.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <TextField
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            label="Weight (lb)"
                            value={row.weight}
                            onChange={(event) =>
                                updateRow(index, { weight: event.target.value })
                            }
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            label="Notes"
                            multiline
                            rows={2}
                            value={row.notes}
                            onChange={(event) =>
                                updateRow(index, { notes: event.target.value })
                            }
                        />
                    </Box>
                ))}
                <Button
                    size="small"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() =>
                        setRows((current) => [...current, emptyPet()])
                    }
                >
                    Add pet
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={isSaving}
                    onClick={handleSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}
